import React, { useEffect, useMemo, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { FiActivity, FiKey, FiPieChart, FiRefreshCw, FiAlertCircle } from 'react-icons/fi';
import { gatewayApi } from '../services/gateway';

const UsageProgress = ({ label, current, limit }) => {
  const safeLimit = Number(limit || 0);
  const percent = safeLimit ? Math.min(100, (Number(current || 0) / safeLimit) * 100) : 0;
  const color = percent >= 90 ? 'bg-red-500' : percent >= 75 ? 'bg-amber-500' : 'bg-blue-600';

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-end gap-3">
        <p className="text-xs font-bold text-slate-700 uppercase tracking-wider">{label}</p>
        <p className="text-[10px] font-mono text-slate-500">
          <span className="text-slate-900 font-bold">{Number(current || 0).toLocaleString()}</span>
          {safeLimit ? ` / ${safeLimit.toLocaleString()}` : ' / Unlimited'}
        </p>
      </div>
      <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden border border-slate-200/50">
        <div className={`h-full rounded-full transition-all duration-700 ${color}`} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
};

const Usage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await gatewayApi.usageSummary();
      setData(response.data);
    } catch (err) {
      setError(err.message || 'Unable to load usage data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const globalLimits = useMemo(() => {
    const clients = data?.clients || [];
    return clients.reduce((acc, client) => ({
      monthly: acc.monthly + Number(client.monthlyLimit || 0),
      annual: acc.annual + Number(client.annualLimit || 0)
    }), { monthly: 0, annual: 0 });
  }, [data]);

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-10">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Consumption & Quotas</h2>
          <p className="text-sm text-slate-500">Live gateway usage calculated from PocketBase delivery logs.</p>
        </div>
        <button onClick={load} className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-600 flex items-center gap-2 hover:bg-slate-50">
          <SafeIcon icon={FiRefreshCw} /> Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 flex gap-3 text-sm">
          <SafeIcon icon={FiAlertCircle} className="mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <h3 className="font-bold text-slate-900 flex items-center gap-2">
          <SafeIcon icon={FiPieChart} className="text-blue-500" /> Global Sending Volume
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-6">
          <UsageProgress label="Today" current={data?.global?.daily || 0} limit={0} />
          <UsageProgress label="This Month" current={data?.global?.monthly || 0} limit={globalLimits.monthly} />
          <UsageProgress label="This Year" current={data?.global?.annual || 0} limit={globalLimits.annual} />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <SafeIcon icon={FiKey} className="text-slate-400" />
          <div>
            <h3 className="font-bold text-slate-900 text-sm">API Key Consumption</h3>
            <p className="text-xs text-slate-500">Counts are evaluated from successful send records.</p>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {loading ? (
            <div className="py-10 text-center text-slate-400">Loading usage from PocketBase...</div>
          ) : (data?.apiKeys || []).length === 0 ? (
            <div className="py-10 text-center text-slate-400">No API keys yet.</div>
          ) : data.apiKeys.map(key => (
            <div key={key.id} className="p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-bold text-slate-900">{key.name}</p>
                  <p className="text-xs text-slate-500 font-mono mt-1">{key.maskedKey || key.keyPrefix}</p>
                </div>
                <div className="grid grid-cols-3 gap-4 text-right">
                  <Mini label="Total" value={key.usage?.total || 0} />
                  <Mini label="Month" value={key.usage?.monthly || 0} />
                  <Mini label="Year" value={key.usage?.annual || 0} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-4">
                <UsageProgress label="Included Allowance" current={key.usage?.total || 0} limit={key.includedEmailLimit || 0} />
                <UsageProgress label="Monthly Limit" current={key.usage?.monthly || 0} limit={key.monthlyLimit || 0} />
                <UsageProgress label="Annual Limit" current={key.usage?.annual || 0} limit={key.annualLimit || 0} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-slate-900 rounded-2xl p-5 text-white flex items-start gap-3">
        <SafeIcon icon={FiActivity} className="text-emerald-400 mt-0.5" />
        <div>
          <p className="text-sm font-bold">Quota enforcement is active</p>
          <p className="text-xs text-slate-400 mt-1">
            Included, monthly, annual and per-minute policies are checked before the provider is called.
          </p>
        </div>
      </div>
    </div>
  );
};

const Mini = ({ label, value }) => (
  <div>
    <p className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">{label}</p>
    <p className="text-sm font-bold text-slate-800">{Number(value || 0).toLocaleString()}</p>
  </div>
);

export default Usage;